/**
 * Game clock.
 *
 * Time is stored as a single integer count of minutes since the start of day 1,
 * and everything else — hour, day, season, year — is derived. One number is
 * trivial to save, cannot drift out of sync with itself, and makes "advance to
 * tomorrow morning" arithmetic rather than a state machine.
 */

export const MINUTES_PER_HOUR = 60;
export const HOURS_PER_DAY = 24;
export const MINUTES_PER_DAY = MINUTES_PER_HOUR * HOURS_PER_DAY;
export const DAYS_PER_SEASON = 28;

export const SEASONS = ['spring', 'summer', 'autumn', 'winter'] as const;
export type Season = (typeof SEASONS)[number];

export const SEASON_NAMES: Readonly<Record<Season, string>> = {
  spring: 'Spring',
  summer: 'Summer',
  autumn: 'Autumn',
  winter: 'Winter',
};

/**
 * Game minutes per real second.
 *
 * 1.5 puts a full day at 16 real minutes — long enough that a morning of
 * farming is not over before it starts, short enough that waiting for evening
 * is never tedious.
 */
export const DEFAULT_TIME_SCALE = 1.5;

/** Katie wakes at 6am, and sleeping past 2am collapses into the next day. */
export const WAKE_HOUR = 6;
export const COLLAPSE_HOUR = 26; // 2am the following day

export interface ClockOptions {
  /** Minutes since the start of day 1. */
  readonly totalMinutes?: number;
  readonly timeScale?: number;
}

export class GameClock {
  private minutes: number;
  readonly timeScale: number;

  constructor(options: ClockOptions = {}) {
    this.timeScale = options.timeScale ?? DEFAULT_TIME_SCALE;
    this.minutes = options.totalMinutes ?? WAKE_HOUR * MINUTES_PER_HOUR;
  }

  /** Advance by real seconds. */
  advance(deltaSeconds: number): void {
    if (!Number.isFinite(deltaSeconds) || deltaSeconds <= 0) return;
    this.minutes += deltaSeconds * this.timeScale;
  }

  get totalMinutes(): number {
    return this.minutes;
  }

  /** Whole minutes elapsed today, 0..1439. */
  get minuteOfDay(): number {
    return Math.floor(this.minutes) % MINUTES_PER_DAY;
  }

  get hour(): number {
    return Math.floor(this.minuteOfDay / MINUTES_PER_HOUR);
  }

  get minute(): number {
    return this.minuteOfDay % MINUTES_PER_HOUR;
  }

  /** Days elapsed since the start, 0-based. */
  get dayIndex(): number {
    return Math.floor(this.minutes / MINUTES_PER_DAY);
  }

  /** Day within the season, 1-based, as a player would say it. */
  get dayOfSeason(): number {
    return (this.dayIndex % DAYS_PER_SEASON) + 1;
  }

  get season(): Season {
    const index = Math.floor(this.dayIndex / DAYS_PER_SEASON) % SEASONS.length;
    return SEASONS[index]!;
  }

  /** Year, 1-based. */
  get year(): number {
    return Math.floor(this.dayIndex / (DAYS_PER_SEASON * SEASONS.length)) + 1;
  }

  /**
   * Jump to 6am on the next day.
   *
   * Uses dayIndex rather than adding a fixed offset so that sleeping at 11pm
   * and collapsing at 2am both land on the same morning.
   */
  sleepUntilMorning(): void {
    const nextDay = this.dayIndex + 1;
    this.minutes = nextDay * MINUTES_PER_DAY + WAKE_HOUR * MINUTES_PER_HOUR;
  }

  /**
   * True once Katie should have passed out where she stands.
   *
   * The window runs from 2am to the 6am wake time — after midnight the clock
   * has already rolled over, so this is a plain range on the small hours rather
   * than anything to do with COLLAPSE_HOUR's 26 notation.
   */
  get isPastCollapse(): boolean {
    return this.hour >= COLLAPSE_HOUR - HOURS_PER_DAY && this.hour < WAKE_HOUR;
  }

  setTotalMinutes(totalMinutes: number): void {
    this.minutes = Number.isFinite(totalMinutes) && totalMinutes >= 0 ? totalMinutes : 0;
  }
}

/** 12-hour clock, as the HUD shows it. */
export function formatTime(hour: number, minute: number): string {
  const suffix = hour < 12 ? 'am' : 'pm';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${minute.toString().padStart(2, '0')}${suffix}`;
}

export function formatDate(season: Season, dayOfSeason: number): string {
  return `${SEASON_NAMES[season]} ${dayOfSeason}`;
}
